import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  detailSourceKey,
  isToolDetailsResponse,
  type HistoryToolDetailSource,
  type ToolDetailSource,
  type ToolDetailsResponse,
} from "@/lib/tool-details";
import i18n from "@/lib/i18n";

export type HistoricalToolDetailsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: ToolDetailsResponse }
  | { status: "fetch_error" };

interface ToolGroupState {
  open: boolean;
  showAll: boolean;
}

interface ResolverState {
  expanded: Record<string, boolean | undefined>;
  entries: Record<string, HistoricalToolDetailsState | undefined>;
  groups: Record<string, ToolGroupState>;
}

const IDLE: HistoricalToolDetailsState = { status: "idle" };
const EMPTY_STATE: ResolverState = { expanded: {}, entries: {}, groups: {} };
const MAX_CONCURRENT_WORKFLOW_PRELOADS = 4;

export interface ToolDetailsResolver {
  isExpanded: (source: ToolDetailSource) => boolean;
  stateFor: (source: ToolDetailSource) => HistoricalToolDetailsState;
  setOpen: (source: ToolDetailSource, open: boolean) => void;
  retry: (source: ToolDetailSource) => void;
  preloadWorkflowRun: (source: ToolDetailSource, name: string) => void;
  /**
   * Resolve disclosure for the current render. `streaming` describes the
   * overall run, while `active` identifies the latest group in that run.
   * An explicit user choice remains authoritative across row remounts.
   */
  isGroupOpen: (id: string, streaming: boolean, active: boolean) => boolean;
  setGroupOpen: (id: string, open: boolean) => void;
  isGroupShowAll: (id: string) => boolean;
  setGroupShowAll: (id: string, showAll: boolean, open?: boolean) => void;
}

/**
 * Session-scoped resolver for canonical tool details.
 *
 * History details load on disclosure, except for mounted workflow launch
 * badges, which preload once per message so their run links can be shown.
 * Live and unavailable sources never construct a URL or acquire fetch state.
 */
export function useToolDetails(
  sessionId: string,
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>,
): ToolDetailsResolver {
  const [state, setState] = useState<ResolverState>(EMPTY_STATE);
  const stateRef = useRef(state);
  const sessionRef = useRef(sessionId);
  const controllersRef = useRef(new Map<string, AbortController>());
  const preloadedWorkflowRunsRef = useRef(new Set<string>());
  const workflowPreloadQueueRef = useRef<HistoryToolDetailSource[]>([]);
  const activeWorkflowPreloadsRef = useRef(0);
  const preloadGenerationRef = useRef(0);

  const updateState = useCallback((updater: (current: ResolverState) => ResolverState) => {
    setState((current) => {
      const next = updater(current);
      stateRef.current = next;
      return next;
    });
  }, []);

  const abortKey = useCallback((key: string) => {
    const controller = controllersRef.current.get(key);
    if (controller) {
      controller.abort();
      controllersRef.current.delete(key);
    }
  }, []);

  useEffect(() => {
    sessionRef.current = sessionId;
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current.clear();
    preloadedWorkflowRunsRef.current.clear();
    workflowPreloadQueueRef.current = [];
    activeWorkflowPreloadsRef.current = 0;
    preloadGenerationRef.current += 1;
    stateRef.current = EMPTY_STATE;
    setState(EMPTY_STATE);

    return () => {
      for (const controller of controllersRef.current.values()) controller.abort();
      controllersRef.current.clear();
      workflowPreloadQueueRef.current = [];
      activeWorkflowPreloadsRef.current = 0;
      preloadGenerationRef.current += 1;
    };
  }, [sessionId]);

  const load = useCallback(async (
    source: HistoryToolDetailSource,
    force = false,
  ) => {
    // A stale virtualized badge can briefly outlive a session transition.
    // Never issue a request for that previous session; the cache is scoped to
    // the active session rather than merely discarding the response later.
    if (source.sessionId !== sessionRef.current) return;
    const key = detailSourceKey(source);
    const existing = stateRef.current.entries[key] ?? IDLE;
    if (!force && (existing.status === "ready" || existing.status === "loading")) return;
    if (controllersRef.current.has(key)) return;

    const controller = new AbortController();
    controllersRef.current.set(key, controller);
    updateState((current) => ({
      ...current,
      entries: { ...current.entries, [key]: { status: "loading" } },
    }));

    try {
      const response = await fetchWithAuth(
        `/api/sessions/${encodeURIComponent(source.sessionId)}/messages/${encodeURIComponent(source.messageId)}/tool-details`,
        { headers: {}, signal: controller.signal },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload: unknown = await response.json();
      if (
        !isToolDetailsResponse(payload) ||
        payload.message_id !== source.messageId
      ) {
        throw new Error(i18n.t("tool.errors.malformedDetail"));
      }

      if (
        sessionRef.current !== source.sessionId ||
        controllersRef.current.get(key) !== controller
      ) {
        return;
      }
      controllersRef.current.delete(key);
      updateState((current) => ({
        ...current,
        entries: { ...current.entries, [key]: { status: "ready", data: payload } },
      }));
    } catch (error) {
      if (
        sessionRef.current !== source.sessionId ||
        controllersRef.current.get(key) !== controller
      ) {
        return;
      }
      controllersRef.current.delete(key);
      if (error instanceof Error && error.name === "AbortError") {
        updateState((current) => ({
          ...current,
          entries: { ...current.entries, [key]: IDLE },
        }));
        return;
      }
      updateState((current) => ({
        ...current,
        entries: { ...current.entries, [key]: { status: "fetch_error" } },
      }));
    }
  }, [fetchWithAuth, updateState]);

  const setOpen = useCallback((source: ToolDetailSource, open: boolean) => {
    const key = detailSourceKey(source);
    updateState((current) => ({
      ...current,
      expanded: { ...current.expanded, [key]: open },
    }));

    if (source.kind !== "history" || source.sessionId !== sessionRef.current) return;
    if (!open) {
      abortKey(key);
      updateState((current) => {
        if (current.entries[key]?.status !== "loading") return current;
        return {
          ...current,
          entries: { ...current.entries, [key]: IDLE },
        };
      });
      return;
    }
    void load(source);
  }, [abortKey, load, updateState]);

  const retry = useCallback((source: ToolDetailSource) => {
    if (source.kind === "history" && source.sessionId === sessionRef.current) {
      void load(source, true);
    }
  }, [load]);

  const drainWorkflowPreloads = useCallback(function drain() {
    while (activeWorkflowPreloadsRef.current < MAX_CONCURRENT_WORKFLOW_PRELOADS) {
      const source = workflowPreloadQueueRef.current.shift();
      if (!source) break;
      if (source.sessionId !== sessionRef.current) continue;
      const generation = preloadGenerationRef.current;
      activeWorkflowPreloadsRef.current += 1;
      void load(source).finally(() => {
        if (generation !== preloadGenerationRef.current) return;
        activeWorkflowPreloadsRef.current -= 1;
        drain();
      });
    }
  }, [load]);

  const preloadWorkflowRun = useCallback((source: ToolDetailSource, name: string) => {
    if (source.kind !== "history" || source.sessionId !== sessionRef.current
      || (name !== "run_workflow" && name !== "run_workflow_direct")) return;
    const key = detailSourceKey(source);
    if (preloadedWorkflowRunsRef.current.has(key)) return;
    preloadedWorkflowRunsRef.current.add(key);
    workflowPreloadQueueRef.current.push(source);
    drainWorkflowPreloads();
  }, [drainWorkflowPreloads]);

  const setGroupOpen = useCallback((id: string, open: boolean) => {
    updateState((current) => ({
      ...current,
      groups: {
        ...current.groups,
        [id]: {
          open,
          showAll: current.groups[id]?.showAll ?? false,
        },
      },
    }));
  }, [updateState]);

  const setGroupShowAll = useCallback((
    id: string,
    showAll: boolean,
    open?: boolean,
  ) => {
    updateState((current) => ({
      ...current,
      groups: {
        ...current.groups,
        [id]: {
          // The show-all control can be rendered by the declarative
          // streaming default before this group has any stored state. Carry
          // that effective open value into the manual override so clicking
          // "show all" cannot close the group that contains the control.
          open: open ?? current.groups[id]?.open ?? false,
          showAll,
        },
      },
    }));
  }, [updateState]);

  return useMemo(() => ({
    isExpanded: (source) => state.expanded[detailSourceKey(source)] === true,
    stateFor: (source) => source.kind === "history"
      ? state.entries[detailSourceKey(source)] ?? IDLE
      : IDLE,
    setOpen,
    retry,
    preloadWorkflowRun,
    isGroupOpen: (id, streaming, active) => {
      const group = state.groups[id];
      if (group) return group.open;

      // Resolve the automatic default during render. Run-level streaming is
      // intentionally independent of the group's transient render marker:
      // trailing assistant prose can move the group away from the render-list
      // tail without ending the run. Only the latest group gets this default,
      // so completed historical groups remain collapsed.
      return streaming && active;
    },
    setGroupOpen,
    isGroupShowAll: (id) => state.groups[id]?.showAll ?? false,
    setGroupShowAll,
  }), [
    state,
    setOpen,
    retry,
    preloadWorkflowRun,
    setGroupOpen,
    setGroupShowAll,
  ]);
}
